/**
 * Event Builder — single reducer (plan 3.3, D20).
 *
 * State groups mirror the four save endpoints exactly:
 *   type          -> admin/event/save-event-type-page
 *   details       -> admin/event/save-event-details-page
 *   availability  -> admin/event/save-event-availability-page
 *   advanced      -> admin/event/save-event-advanced-page
 *
 * Hydration never marks a step dirty; only user edits (UPDATE_STEP) do.
 * Dirty tracking is version based: `save.version[step]` increments on every edit and
 * `save.savedVersion[step]` is set by the save engine when a request that carried that
 * version succeeds — so an edit made while a request is in flight keeps the step dirty.
 */

import {
  ASSIGNMENT_MODE,
  D,
  DEFAULT_COLOR,
  DEFAULT_DURATION,
  DEFAULT_QUESTION,
  E,
  FLAT_PERCENT,
  FORMAT,
  KIND,
  PAYMENT_TYPE,
  REFUND_CUTOFF_DEFAULT_MINUTES,
  REFUND_TYPE,
  SCHEDULE_SOURCE,
  SECTIONS,
  STAFF_ASSIGNMENT_TYPE,
  STEPS,
  STEP_ORDER,
  VALIDITY,
  CONFIRMATION_PAGE,
  INVITEE_CALL_STATUS,
  VENUE_LOCATION_STATUS,
  VENUE_OTHER_DISPLAY_STATUS,
} from "./constants";

// ---- Defaults (plan 9.2) ---------------------------------------------------------------------

export const defaultType = () => ({
  event_name: "",
  event_url: "",
  urlTouched: false,
  event_format_id: FORMAT.ONE_TO_ONE,
  no_of_attendies: 1,
  venue_id: "",
  venue_location: "",
  venue_location_notes: "",
  venue_location_status: VENUE_LOCATION_STATUS.DISPLAY_BOOKING,
  invitee_call_status: INVITEE_CALL_STATUS.ME,
  host_phone_no: "",
  venue_other_notes: "",
  venue_other_display_status: VENUE_OTHER_DISPLAY_STATUS.BOOKING_NOTES,
});

export const defaultDetailsFree = () => ({
  event_image: null, // File | null — only sent when a File (3.4 rule 9)
  event_image_url: "", // existing image path from the GET (display only)
  event_color: DEFAULT_COLOR,
  event_description: "",
  max_no_booking_status: D,
  max_no_of_booking: 0,
  recurring_booking_status: D,
  allow_rescheduling_status: E,
  attendee_cancellation_status: E,
  cancellation_policy: "",
  instruction_to_attendee: "",
  ita_booking_page_status: D,
  ita_confirmation_page_status: D,
  ita_reminder_email_status: D,
});

export const defaultDetailsPaid = (eventName = "") => ({
  ...defaultDetailsFree(),
  pricing: [{ item_name: eventName, price: "", qty: 1 }],
  pricingNameTouched: false, // UI-only (not sent): pricing[0].item_name follows the event name until edited
  add_ons: [],
  event_prepaid_status: E,
  payments_type: PAYMENT_TYPE.FULL,
  payments_flat_type: FLAT_PERCENT.PERCENT,
  payments_price: "",
  deposit_type: "",
  deposit_flat_per_type: FLAT_PERCENT.PERCENT,
  deposit_price: "",
  coupon_codes_status: D,
  recurrent_booking_discounts_status: D,
  staff_commission_status: D,
  staff_commission_rate: 0,
  refund_type: REFUND_TYPE.FULL,
  refund_percentage: "",
  refund_cutoff_time: REFUND_CUTOFF_DEFAULT_MINUTES,
});

export const defaultDetails = (kind, eventName) =>
  kind === KIND.PAID ? defaultDetailsPaid(eventName) : defaultDetailsFree();

export const defaultAvailability = () => ({
  event_duration_in_minutes: DEFAULT_DURATION,
  invitee_set_duration_status: D,
  slot_duration_increment_of: 0,
  buffer_before_event_minutes: 0,
  buffer_after_event_minutes: 0,
  min_scheduling_notice_minutes: 0,
  max_scheduling_notice_minutes: 0,
  max_event_per_day: 0,
  max_event_per_week: 0,
  is_time_block: D, // constant, no UI (old page: "Time block UI removed")
  event_internal_notes: "",
  timezone_id: "",
  event_validity: VALIDITY.INDEFINITE,
  valid_over_period: "",
  event_start_date: "",
  event_end_date: "",
  scheduleSource: SCHEDULE_SOURCE.TEMPLATE,
  templateId: "",
  // exact shape today's Event-Availbility-Component produces
  // [{ type:'event'|'event_staff', staffId, timezoneId, schedules:[{dayId, dated, schedules:[{start_time,end_time}]}] }]
  schedules: [],
  staff_assignment_type: STAFF_ASSIGNMENT_TYPE.MY_EVENT,
  assignmentMode: ASSIGNMENT_MODE.MY,
  staff_choose: "",
  staff_assignment_method: "",
  staffs: [],
});

export const defaultAdvanced = () => ({
  event_public_visiblity_status: E,
  confirmation_page: CONFIRMATION_PAGE.DEFAULT,
  confirmation_page_url: "",
  customer_feedback_status: D,
  invitee_guests_status: D,
  staff_can_confirm_booking: D,
  email_reminder_status: D,
  sms_reminder_status: D,
  email_followup_status: D,
  questions: [{ ...DEFAULT_QUESTION, details: [] }],
});

export const defaultLookups = () => ({
  venues: [], // GET venues → [{id, venue, venue_code}]
  connectedVenues: [], // GET admin/connected-venues/{businessId} → [{app_id, connection_status}]
  connectedVenuesLoaded: false,
  connectedCalendars: [], // GET admin/get-connected-calendars/{businessId} → entity[]
  scheduleTemplates: [], // GET get-schedule-templates → [{value,label,schedule_type,is_default,is_business_hours,staff_id}]
  scheduleTemplatesLoaded: false,
  timezones: [], // GET /timezones → [{value,label}] (see hydrate.normalizeTimezones)
  staff: [], // POST admin/staffs/{businessId} → filtered active staff
  staffLoaded: false,
  eventConfig: null, // GET admin/settings/event/{businessId}
  orgStaffCanConfirm: D, // advanced GET org_staff_can_confirm_booking
  paymentConnected: null, // null = unknown, true/false from payment-integration-status
  paymentStatusLoaded: false,
  paymentCurrency: "",
  datePickerFormat: "mm/dd/yyyy",
  calendarFirstDay: "1",
  hadRoundRobin: false,
  hadStaffConfirm: false,
  loadedIsPaid: null, // is_event_paid from the type GET (edit) — drives the payment guard
});

const emptyStepMap = (value) =>
  STEP_ORDER.reduce((acc, step) => {
    acc[step] = typeof value === "function" ? value() : value;
    return acc;
  }, {});

export const initialBuilderState = ({ eventId = null, kind = KIND.FREE, section = SECTIONS.QUICK } = {}) => {
  const defaultName = eventId ? "" : "New Event";
  return {
  meta: {
    eventId: eventId ? parseInt(eventId, 10) : null,
    isNew: !eventId,
    isDraft: E,
    isActive: D,
    kind,
    section,
    loading: Boolean(eventId), // edit: skeleton until the four GETs resolve (9.1)
    loadError: null,
    published: false, // success screen shown
    publishedKind: null,
    kindRehydrate: false, // set by SET_KIND on an existing event; cleared after re-hydration (3.4 rule 13)
  },
  type: { ...defaultType(), event_name: defaultName, event_url: eventId ? "" : "new-event" },
  details: defaultDetails(kind, defaultName),
  availability: defaultAvailability(),
  advanced: defaultAdvanced(),
  lookups: defaultLookups(),
  save: {
    status: "idle", // idle | saving | saved | error
    version: emptyStepMap(0),
    savedVersion: emptyStepMap(0),
    lastError: null,
    fieldErrors: emptyStepMap(() => ({})), // step -> { field: message }
    sectionErrors: {}, // section -> message (non-field server errors)
  },
  ui: {
    previewOpen: false,
    toast: null, // { id, text }
    confirm: null, // { title, body, confirmLabel, danger, onConfirm }
    highlight: null, // block id
    navOpen: false, // mobile section dropdown
  },
};
};

// ---- Actions ----------------------------------------------------------------------------------

export const A = {
  HYDRATE: "HYDRATE",
  SET_META: "SET_META",
  SET_LOOKUPS: "SET_LOOKUPS",
  UPDATE_STEP: "UPDATE_STEP",
  REPLACE_STEP: "REPLACE_STEP",
  SET_KIND: "SET_KIND",
  SAVE_STATUS: "SAVE_STATUS",
  SAVE_SUCCESS: "SAVE_SUCCESS",
  SET_FIELD_ERRORS: "SET_FIELD_ERRORS",
  CLEAR_FIELD_ERRORS: "CLEAR_FIELD_ERRORS",
  SET_SECTION_ERROR: "SET_SECTION_ERROR",
  MARK_ALL_DIRTY: "MARK_ALL_DIRTY",
  UI: "UI",
};

export const isStep = (step) => STEP_ORDER.includes(step);

export function builderReducer(state, action) {
  switch (action.type) {
    case A.HYDRATE: {
      // payload: { meta?, type?, details?, availability?, advanced?, lookups? } — merged, not dirty
      const p = action.payload || {};
      return {
        ...state,
        meta: { ...state.meta, ...(p.meta || {}) },
        type: p.type ? { ...state.type, ...p.type } : state.type,
        details: p.details ? { ...state.details, ...p.details } : state.details,
        availability: p.availability ? { ...state.availability, ...p.availability } : state.availability,
        advanced: p.advanced ? { ...state.advanced, ...p.advanced } : state.advanced,
        lookups: p.lookups ? { ...state.lookups, ...p.lookups } : state.lookups,
      };
    }

    case A.SET_META:
      return { ...state, meta: { ...state.meta, ...action.payload } };

    case A.SET_LOOKUPS:
      return { ...state, lookups: { ...state.lookups, ...action.payload } };

    case A.UPDATE_STEP: {
      const { step } = action;
      if (!isStep(step)) return state;
      // a function patch receives the *current* step state — used by async edits (template fetch →
      // staff rows) so they can never overwrite an edit that landed while the request was in flight
      const patch = typeof action.patch === "function" ? action.patch(state[step]) : action.patch;
      return {
        ...state,
        [step]: { ...state[step], ...patch },
        save: {
          ...state.save,
          version: { ...state.save.version, [step]: state.save.version[step] + 1 },
        },
      };
    }

    case A.REPLACE_STEP: {
      // whole-step replacement that is NOT a user edit (kind re-hydration, 3.4 rule 13)
      const { step, value } = action;
      if (!isStep(step)) return state;
      return { ...state, [step]: value };
    }

    case A.SET_KIND: {
      const { kind } = action;
      if (kind === state.meta.kind) return state;
      const isExisting = Boolean(state.meta.eventId);
      // Details shape follows the kind. For a NEW event we swap the local defaults (name kept
      // for pricing[0]); for an existing event the engine re-fetches the right details page
      // after the type save is acknowledged (rule 13), so we keep current values until then.
      const details = isExisting
        ? state.details
        : kind === KIND.PAID
        ? { ...defaultDetailsPaid(state.type.event_name), ...pickShared(state.details) }
        : { ...defaultDetailsFree(), ...pickShared(state.details) };
      return {
        ...state,
        meta: { ...state.meta, kind, kindRehydrate: isExisting },
        details,
        save: {
          ...state.save,
          version: {
            ...state.save.version,
            [STEPS.TYPE]: state.save.version[STEPS.TYPE] + 1,
            [STEPS.DETAILS]: state.save.version[STEPS.DETAILS] + 1,
          },
        },
      };
    }

    case A.SAVE_STATUS:
      return {
        ...state,
        save: {
          ...state.save,
          status: action.status,
          lastError: action.status === "error" ? action.error || state.save.lastError : null,
        },
      };

    case A.SAVE_SUCCESS: {
      const { step, version } = action;
      return {
        ...state,
        save: {
          ...state.save,
          savedVersion: { ...state.save.savedVersion, [step]: Math.max(state.save.savedVersion[step], version) },
          fieldErrors: { ...state.save.fieldErrors, [step]: {} },
        },
      };
    }

    case A.SET_FIELD_ERRORS:
      return {
        ...state,
        save: { ...state.save, fieldErrors: { ...state.save.fieldErrors, [action.step]: action.errors || {} } },
      };

    case A.CLEAR_FIELD_ERRORS: {
      const { step, field } = action;
      const current = state.save.fieldErrors[step] || {};
      if (!field) {
        return { ...state, save: { ...state.save, fieldErrors: { ...state.save.fieldErrors, [step]: {} } } };
      }
      if (!(field in current)) return state;
      const next = { ...current };
      delete next[field];
      return { ...state, save: { ...state.save, fieldErrors: { ...state.save.fieldErrors, [step]: next } } };
    }

    case A.SET_SECTION_ERROR: {
      const next = { ...state.save.sectionErrors };
      if (action.message) next[action.section] = action.message;
      else delete next[action.section];
      return { ...state, save: { ...state.save, sectionErrors: next } };
    }

    case A.MARK_ALL_DIRTY: {
      // publish: every step is sent, dirty or not — bump versions so SAVE_SUCCESS bookkeeping is exact
      const version = { ...state.save.version };
      STEP_ORDER.forEach((s) => {
        version[s] += 1;
      });
      return { ...state, save: { ...state.save, version } };
    }

    case A.UI:
      return { ...state, ui: { ...state.ui, ...action.payload } };

    default:
      return state;
  }
}

/** Fields that exist in both the free and paid details shapes (kept across a kind switch). */
function pickShared(details) {
  const free = defaultDetailsFree();
  const out = {};
  Object.keys(free).forEach((k) => {
    if (k in details) out[k] = details[k];
  });
  return out;
}

// ---- Action creators ----------------------------------------------------------------------------

export const actions = {
  hydrate: (payload) => ({ type: A.HYDRATE, payload }),
  setMeta: (payload) => ({ type: A.SET_META, payload }),
  setLookups: (payload) => ({ type: A.SET_LOOKUPS, payload }),
  update: (step, patch) => ({ type: A.UPDATE_STEP, step, patch }),
  replaceStep: (step, value) => ({ type: A.REPLACE_STEP, step, value }),
  setKind: (kind) => ({ type: A.SET_KIND, kind }),
  saveStatus: (status, error) => ({ type: A.SAVE_STATUS, status, error }),
  saveSuccess: (step, version) => ({ type: A.SAVE_SUCCESS, step, version }),
  setFieldErrors: (step, errors) => ({ type: A.SET_FIELD_ERRORS, step, errors }),
  clearFieldErrors: (step, field) => ({ type: A.CLEAR_FIELD_ERRORS, step, field }),
  setSectionError: (section, message) => ({ type: A.SET_SECTION_ERROR, section, message }),
  markAllDirty: () => ({ type: A.MARK_ALL_DIRTY }),
  ui: (payload) => ({ type: A.UI, payload }),
};

export const isStepDirty = (save, step) => save.version[step] > save.savedVersion[step];
export const anyDirty = (save) => STEP_ORDER.some((s) => isStepDirty(save, s));
